import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryList, ViewQueryRecordNode } from '@salescore/core'
import { Array, Equivalence, Result } from 'effect'

import { getLabeledFieldName } from '../../state/util'
import type { DeepReadonly } from '../types/DeepReadonly'
import { isNewRecord } from './isNewRecord'
import type { RecordChange } from './updateViewRecords'

export type UpdateRootViewRecordLeft =
  | {
      message: 'このブロックは書き込み禁止です'
    }
  | {
      message: `{{field}}は作成禁止です` | `{{field}}は更新禁止です`
      arguments: { field: string }
    }
  | undefined

export function updateRootViewRecord(
  { rowIndex, innerRowIndex, field, value, label }: DeepReadonly<RecordChange>,
  query: DeepReadonly<ViewQueryList>,
  newRootRecordNode: DeepReadonly<ViewQueryRecordNode>,
): Result.Result<
  { viewRecordFieldChanges: ViewRecordFieldChange[]; rootRecordNode: DeepReadonly<ViewQueryRecordNode> },
  UpdateRootViewRecordLeft
> {
  if (field.write === undefined) {
    return Result.fail({
      message: 'このブロックは書き込み禁止です',
    })
  }
  const oldValue = newRootRecordNode.attributes[field.name]
  if (oldValue === value) {
    return Result.fail(undefined) // わざわざユーザに通知しない
  }
  if (field.meta.creatable === false && isNewRecord(newRootRecordNode)) {
    return Result.fail({
      message: `{{field}}は作成禁止です`,
      arguments: { field: field.meta.label },
    })
  }
  if (field.meta.updatable === false && !isNewRecord(newRootRecordNode)) {
    return Result.fail({
      message: `{{field}}は更新禁止です`,
      arguments: { field: field.meta.label },
    })
  }

  // レコードを更新
  const updatedRecordNode = {
    ...newRootRecordNode,
    attributes: {
      ...newRootRecordNode.attributes,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      [field.name]: value as string,
      ...(label === undefined ? {} : { [getLabeledFieldName(field.name)]: label }),
    },
  }

  if (updatedRecordNode.id === undefined) {
    return Result.succeed({ viewRecordFieldChanges: [], rootRecordNode: updatedRecordNode })
  }
  const nameField = query.fields
    .filter((x) => Array.makeEquivalence(Equivalence.String)(x.nodePath, query.tree.path))
    .find((field) => field.meta.fieldMetaType === 'name')
  return Result.succeed({
    viewRecordFieldChanges: [
      {
        id: updatedRecordNode.id,
        streamName: field.write.streamName,
        fieldChanges: [
          {
            id: updatedRecordNode.id,
            fieldName: field.name,
            propertySourceName: field.write.propertySourceName,
            value,
            valueLabel: label,
            rowIndex,
            innerRowIndex,
          },
        ],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        recordNameLabel: nameField === undefined ? undefined : (updatedRecordNode.attributes[nameField.name] as string),
        before: {
          [field.write.propertySourceName]: oldValue,
        },
        after: {
          [field.write.propertySourceName]: value,
        },
      },
    ],
    rootRecordNode: updatedRecordNode,
  })
}
